import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Phone,
  ChevronDown,
} from "lucide-react";

const BRETAGNE_BG =
  "https://images.pexels.com/photos/31691918/pexels-photo-31691918.jpeg?auto=compress&cs=tinysrgb&w=1920";

// Pour qui ?
const pourQui = [
  { label: "Artisans" },
  { label: "Commerçants" },
  { label: "Indépendants" },
  { label: "Professions libérales" },
];

// FAQ — 3 questions essentielles
const faq = [
  {
    q: "Combien de temps faut-il pour créer mon site ?",
    a: "Entre 2 et 4 semaines en moyenne, selon vos disponibilités pour valider le contenu et le design.",
  },
  {
    q: "Puis-je modifier mon site moi-même ?",
    a: "Oui. À la livraison, je vous forme rapidement pour que vous puissiez modifier facilement vos textes, photos et informations.",
  },
  {
    q: "Combien coûte la création d'un site ?",
    a: "Chaque site est réalisé sur devis à partir de 600 € HT. Le tarif dépend du nombre de pages, des fonctionnalités et des prestations choisies (identité visuelle, supports, etc.). La majorité des projets se situent entre 600 € et 2 500 € HT.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ────────────── HERO plein écran — Bretagne fondue ────────────── */}
      <section
        className="relative min-h-[92vh] w-full flex items-center overflow-hidden"
        data-testid="hero"
      >
        {/* Image de fond */}
        <img
          src={BRETAGNE_BG}
          alt="Côte bretonne — Bretagne, France"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "blur(4px)" }}
          loading="eager"
        />
        {/* Voile assombri navy */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(47,73,110,0.55) 0%, rgba(47,73,110,0.45) 50%, rgba(47,73,110,0.6) 100%)",
          }}
        />
        {/* Voile beige/crème doux pour lisibilité */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(70% 90% at 30% 50%, rgba(247,244,238,0.35) 0%, rgba(247,244,238,0.05) 60%, transparent 100%)",
          }}
        />
        {/* Grain doux */}
        <div className="absolute inset-0 grain opacity-30" />

        {/* Contenu hero */}
        <div className="relative z-10 container-up py-24 md:py-32 w-full">
          <div className="max-w-3xl reveal">
            <span className="inline-flex items-center gap-2 rounded-full bg-up-offwhite/95 backdrop-blur ring-1 ring-white/30 px-4 py-1.5 text-[11px] font-medium tracking-[0.22em] uppercase text-up-brown shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)]">
              <span
                className="inline-flex h-1.5 w-1.5 rounded-full"
                style={{ background: "#DCC6A3" }}
              />
              Studio bretonnais · Sur devis
            </span>

            <h1
              className="mt-6 font-serif text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.02] tracking-tight text-up-offwhite"
              data-testid="hero-headline"
            >
              Attirez plus de clients grâce à un site internet
              <em className="italic font-light" style={{ color: "#DCC6A3" }}>
                {" "}
                professionnel.
              </em>
            </h1>

            <p className="mt-7 max-w-2xl text-up-sand/95 text-lg md:text-xl leading-[1.6]">
              Création de sites internet modernes pour les artisans,
              commerçants et indépendants en Bretagne. Chaque projet est
              conçu sur mesure, selon vos besoins et votre budget.
            </p>

            {/* 3 avantages */}
            <ul className="mt-8 flex flex-wrap gap-3" data-testid="hero-advantages">
              {["Site clé en main", "Responsive mobile", "Optimisé pour Google"].map((a) => (
                <li
                  key={a}
                  className="inline-flex items-center gap-2 rounded-full bg-up-offwhite/12 backdrop-blur ring-1 ring-white/25 px-4 py-2 text-[13px] font-medium text-up-offwhite"
                >
                  <CheckCircle2 className="h-4 w-4 text-up-sand" strokeWidth={2} />
                  {a}
                </li>
              ))}
            </ul>

            {/* Boutons */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-devis"
                className="group inline-flex items-center gap-2 rounded-full bg-up-sand text-up-brown px-8 py-4 text-base font-medium tracking-wide shadow-[0_20px_50px_-15px_rgba(220,198,163,0.55)] transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-offwhite hover:shadow-[0_26px_60px_-15px_rgba(220,198,163,0.75)]"
              >
                Prendre rendez-vous
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/galerie"
                data-testid="hero-cta-realisations"
                className="group inline-flex items-center gap-2 rounded-full border border-up-offwhite/60 text-up-offwhite bg-up-offwhite/10 backdrop-blur px-8 py-4 text-base font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-offwhite hover:text-up-brown"
              >
                Voir mes réalisations
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>

            {/* Bouton d'appel bien visible — label uniquement */}
            <a
              href="tel:0695977072"
              data-testid="hero-cta-phone"
              aria-label="Appeler Breizh Boost Studio"
              className="mt-6 inline-flex items-center gap-3 rounded-full bg-white/95 text-up-brown px-6 py-3.5 font-medium text-base shadow-[0_18px_44px_-16px_rgba(0,0,0,0.5)] ring-1 ring-white/40 transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-offwhite"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-up-brown text-up-sand">
                <Phone className="h-4 w-4" strokeWidth={2} />
              </span>
              Appeler
            </a>

            <div className="mt-8 flex items-center gap-4 text-xs sm:text-sm text-up-sand/85">
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                Disponible pour de nouveaux projets
              </span>
              <span className="h-4 w-px bg-up-offwhite/25" />
              <span>Premier échange gratuit</span>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-up-offwhite/60"
          aria-hidden
        >
          <span className="text-[10px] tracking-[0.32em] uppercase">Découvrir</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* ────────────── PROPOSITION DE VALEUR ────────────── */}
      <section
        className="container-up py-20 md:py-28"
        data-testid="value-prop"
      >
        <div className="max-w-3xl mx-auto text-center reveal">
          <span className="section-label justify-center">Ma promesse</span>
          <h2 className="mt-6 font-serif text-[2rem] sm:text-4xl md:text-[2.75rem] leading-[1.05] tracking-tight">
            Un site internet professionnel,
            <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
              {" "}
              sans complications.
            </em>
          </h2>

          <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-brown/40" />

          <p className="mt-10 text-up-text/90 text-lg md:text-xl leading-[1.9]">
            Je m&apos;occupe de tout&nbsp;: création du site, design personnalisé,
            référencement de base, mise en ligne et accompagnement.
          </p>
          <p className="mt-6 font-serif italic text-xl md:text-2xl text-up-brown leading-[1.5]">
            Vous vous concentrez sur votre métier.
          </p>
        </div>
      </section>


      {/* ────────────── OFFRE TEASER — asymétrie gauche ────────────── */}
      <section
        className="relative overflow-hidden border-y border-up-taupe/30"
        style={{ background: "#FBF6EC" }}
        data-testid="home-pack-teaser"
      >
        <div className="absolute inset-0 depth-soft-light pointer-events-none" aria-hidden />
        <div className="container-up py-24 md:py-36 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Colonne visuelle — capsule prix décorative */}
            <div className="lg:col-span-5 reveal">
              <div className="relative mx-auto max-w-[400px]">
                {/* Halo doux */}
                <div
                  aria-hidden
                  className="absolute -inset-6 -z-10 rounded-[3rem]"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 50% 50%, rgba(220,198,163,0.4) 0%, transparent 70%)",
                  }}
                />
                {/* Carte prix */}
                <div
                  className="float-soft relative aspect-square rounded-[2rem] shadow-[0_40px_80px_-30px_rgba(47,73,110,0.4)] overflow-hidden ring-1 ring-up-brown/10 flex flex-col items-center justify-center text-center px-8"
                  style={{ background: "#2F496E", ["--tilt"]: "-1.5deg" }}
                >
                  <div className="absolute inset-0 grain opacity-50" />
                  <div
                    className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-up-sand/15 blur-3xl"
                  />
                  <div
                    className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-up-sand/10 blur-3xl"
                  />
                  <span className="relative text-[11px] tracking-[0.32em] uppercase text-up-sand/80">
                    Les tarifs
                  </span>
                  <p className="relative mt-5 font-serif text-[2.75rem] sm:text-[3.25rem] leading-[1.1] tracking-tight text-up-offwhite">
                    À partir de
                    <br />
                    600&nbsp;€&nbsp;HT
                  </p>
                  <p className="relative mt-5 font-serif italic text-up-sand text-lg leading-snug">
                    Sur devis.
                    <br />
                    Selon vos besoins.
                  </p>
                </div>
                {/* Badge flottant */}
                <div
                  className="float-soft-delay absolute -bottom-4 -right-4 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase font-medium shadow-[0_18px_40px_-15px_rgba(47,73,110,0.45)] ring-1"
                  style={{
                    background: "#F7F4EE",
                    color: "#2F496E",
                    borderColor: "rgba(47,73,110,0.18)",
                  }}
                  aria-hidden
                >
                  <span
                    className="inline-flex h-1.5 w-1.5 rounded-full"
                    style={{ background: "#DCC6A3" }}
                  />
                  Sur mesure
                </div>
              </div>
            </div>

            {/* Colonne texte */}
            <div className="lg:col-span-7 reveal" style={{ transitionDelay: "150ms" }}>
              <span className="section-label">Mon offre</span>
              <h2 className="mt-6 font-serif text-[2.25rem] sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-tight">
                Un site pensé pour
                <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
                  {" "}
                  votre activité.
                </em>
              </h2>
              <div aria-hidden className="mt-8 h-px w-12 bg-up-brown/40" />
              <p className="mt-8 text-up-text/90 text-lg leading-[1.8] max-w-xl">
                Pas de formule toute faite. Chaque projet est construit selon
                votre activité, vos besoins et votre budget — à partir de{" "}
                <strong className="text-up-brown font-semibold">600 € HT</strong>, sur devis.
              </p>
              <div className="mt-10">
                <Link
                  to="/services"
                  data-testid="home-pack-teaser-cta"
                  className="btn-primary group !px-9 !py-4 !text-base"
                >
                  Découvrir mon offre
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────── RÉALISATIONS TEASER — asymétrie droite ────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: "#F7F4EE" }}
        data-testid="home-realisations-teaser"
      >
        <div className="container-up py-24 md:py-36">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Colonne texte — à gauche */}
            <div className="lg:col-span-6 reveal order-2 lg:order-1">
              <span className="section-label">Mes réalisations</span>
              <h2 className="mt-6 font-serif text-[2.25rem] sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-tight">
                Découvrez mes
                <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
                  {" "}
                  réalisations.
                </em>
              </h2>
              <div aria-hidden className="mt-8 h-px w-12 bg-up-brown/40" />
              <p className="mt-8 text-up-text/90 text-lg leading-[1.8] max-w-xl">
                Des sites conçus sur mesure pour des entreprises locales.
              </p>
              <div className="mt-10">
                <Link
                  to="/galerie"
                  data-testid="home-realisations-teaser-cta"
                  className="btn-primary group !px-9 !py-4 !text-base"
                >
                  Voir toutes mes réalisations
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>

            {/* Colonne visuelle — mockups légers */}
            <div className="lg:col-span-6 relative reveal order-1 lg:order-2" style={{ transitionDelay: "150ms" }}>
              <div className="relative mx-auto max-w-[500px]">
                {/* Halo doux */}
                <div
                  aria-hidden
                  className="absolute -inset-6 -z-10 rounded-[3rem]"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 60% 50%, rgba(47,73,110,0.08) 0%, transparent 70%)",
                  }}
                />

                {/* Mockup navy stylisé — arrière-plan */}
                <div
                  className="float-soft-slow relative w-[85%] ml-auto rounded-2xl overflow-hidden ring-1 shadow-[0_28px_60px_-20px_rgba(47,73,110,0.4)]"
                  style={{
                    background: "#2F496E",
                    borderColor: "rgba(47,73,110,0.2)",
                    ["--tilt"]: "2deg",
                  }}
                  aria-hidden
                >
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10">
                    <span className="h-2 w-2 rounded-full bg-[#E3A292]" />
                    <span className="h-2 w-2 rounded-full bg-[#E6CB8A]" />
                    <span className="h-2 w-2 rounded-full bg-[#9BB89B]" />
                    <span className="ml-3 flex-1 text-[10px] tracking-wider text-up-sand/60 truncate">
                      client-1.bzh
                    </span>
                  </div>
                  <div className="px-6 pt-7 pb-8">
                    <div className="h-2.5 w-2/3 rounded-full mb-3" style={{ background: "#DCC6A3", opacity: 0.85 }} />
                    <div className="h-1.5 w-3/4 rounded-full mb-2" style={{ background: "rgba(220,198,163,0.35)" }} />
                    <div className="h-1.5 w-2/3 rounded-full mb-4" style={{ background: "rgba(220,198,163,0.28)" }} />
                    <div className="flex gap-2 mt-3">
                      <span className="h-6 w-20 rounded-full" style={{ background: "#DCC6A3" }} />
                      <span className="h-6 w-14 rounded-full bg-white/12" />
                    </div>
                  </div>
                </div>

                {/* Mockup crème stylisé — avant-plan flottant */}
                <div
                  className="float-soft absolute -bottom-6 -left-4 sm:-left-8 w-[65%] rounded-2xl overflow-hidden ring-1 shadow-[0_24px_54px_-18px_rgba(47,73,110,0.45)]"
                  style={{
                    background: "#F7F4EE",
                    borderColor: "rgba(47,73,110,0.2)",
                    ["--tilt"]: "-3deg",
                  }}
                  aria-hidden
                >
                  <div
                    className="flex items-center gap-1.5 px-3 py-2 border-b"
                    style={{
                      background: "rgba(47,73,110,0.06)",
                      borderColor: "rgba(47,73,110,0.12)",
                    }}
                  >
                    <span className="h-2 w-2 rounded-full bg-[#E3A292]" />
                    <span className="h-2 w-2 rounded-full bg-[#E6CB8A]" />
                    <span className="h-2 w-2 rounded-full bg-[#9BB89B]" />
                    <span className="ml-3 flex-1 text-[10px] tracking-wider truncate" style={{ color: "#6C7A89" }}>
                      client-2.fr
                    </span>
                  </div>
                  <div className="px-5 pt-5 pb-5">
                    <div className="h-2 w-1/2 rounded-full mb-2" style={{ background: "#2F496E", opacity: 0.7 }} />
                    <div className="h-1.5 w-3/4 rounded-full mb-1.5" style={{ background: "rgba(47,73,110,0.18)" }} />
                    <div className="h-1.5 w-2/3 rounded-full mb-3" style={{ background: "rgba(47,73,110,0.14)" }} />
                  </div>
                </div>

                {/* Badge "En ligne" flottant */}
                <div
                  className="float-soft-delay absolute top-4 right-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-medium ring-1 shadow-[0_14px_30px_-12px_rgba(47,73,110,0.45)]"
                  style={{
                    background: "rgba(247,244,238,0.95)",
                    color: "#2F496E",
                    borderColor: "rgba(47,73,110,0.18)",
                  }}
                  aria-hidden
                >
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Sites en ligne
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────── POUR QUI ? — navy contraste ────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: "#2F496E" }}
        data-testid="home-pour-qui"
      >
        <div className="absolute inset-0 grain opacity-40" aria-hidden />
        <div
          className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-up-sand/8 blur-3xl"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-up-sand/6 blur-3xl"
          aria-hidden
        />
        <div className="container-up py-24 md:py-32 relative">
          <div className="max-w-3xl mx-auto text-center mb-14 md:mb-16 reveal">
            <span className="inline-block text-[11px] sm:text-xs tracking-[0.32em] uppercase font-medium text-up-sand/90">
              Pour qui&nbsp;?
            </span>
            <h2 className="mt-6 font-serif text-[2rem] sm:text-4xl md:text-[2.75rem] leading-[1.05] tracking-tight !text-up-offwhite">
              Une solution pensée pour les
              <em className="italic font-normal text-up-sand">
                {" "}
                professionnels locaux.
              </em>
            </h2>
            <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-sand/50" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
            {pourQui.map((p, i) => (
              <article
                key={p.label}
                data-testid={`home-pour-qui-${i}`}
                className="reveal hover-card rounded-2xl bg-up-offwhite/95 backdrop-blur ring-1 ring-up-sand/15 px-5 py-8 md:px-6 md:py-10 text-center"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full font-serif italic text-lg"
                  style={{ background: "#DCC6A3", color: "#2F496E" }}
                  aria-hidden
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-5 font-serif text-lg md:text-xl text-up-brown tracking-tight">
                  {p.label}
                </h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── FAQ ────────────── */}
      <section
        className="relative py-24 md:py-32"
        style={{ background: "#FBF6EC" }}
        data-testid="home-faq"
      >
        <div className="absolute inset-0 depth-soft-light pointer-events-none" aria-hidden />
        <div className="container-up relative">
          <div className="max-w-3xl mx-auto text-center mb-12 reveal">
            <span className="section-label justify-center">Vos questions</span>
            <h2 className="mt-6 font-serif text-[2rem] sm:text-4xl md:text-[2.5rem] leading-[1.1] tracking-tight">
              Les questions
              <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
                {" "}
                les plus fréquentes.
              </em>
            </h2>
            <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-brown/40" />
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faq.map((item, i) => (
              <details
                key={item.q}
                data-testid={`home-faq-item-${i}`}
                className="reveal group rounded-2xl bg-up-offwhite ring-1 ring-up-taupe/40 overflow-hidden transition-all duration-500 hover:ring-up-brown/40 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.28)]"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                  <span className="font-serif text-lg md:text-xl text-up-brown">
                    {item.q}
                  </span>
                  <span
                    aria-hidden
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-up-sand/50 text-up-brown transition-transform duration-500 group-open:rotate-180"
                  >
                    <ChevronDown className="h-4 w-4" strokeWidth={2} />
                  </span>
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-up-text/90 leading-[1.75] text-[15px]">
                    {item.a}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── CTA FINAL ────────────── */}
      <section
        className="container-up pt-16 md:pt-20 pb-24 md:pb-32"
        data-testid="home-final-cta"
      >
        <div className="relative overflow-hidden rounded-3xl bg-up-brown text-up-offwhite px-6 sm:px-10 md:px-20 py-20 sm:py-24 md:py-28 text-center">
          <div className="absolute inset-0 grain" />
          <div
            className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-up-sand/10 blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-up-sand/5 blur-3xl"
            aria-hidden
          />

          <div className="relative z-10 max-w-2xl mx-auto reveal">
            <span className="inline-block text-[11px] sm:text-xs tracking-[0.32em] uppercase font-medium text-up-sand/90">
              On en discute&nbsp;?
            </span>
            <h2 className="mt-6 font-serif text-[2.25rem] sm:text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-tight !text-up-offwhite">
              Prêt à développer
              <em className="italic font-normal text-up-sand"> votre visibilité&nbsp;?</em>
            </h2>

            <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-sand/50" />

            <p className="mt-10 text-up-sand leading-[1.85] text-base sm:text-lg max-w-xl mx-auto">
              Un site professionnel, moderne et efficace pour présenter votre
              activité et trouver de nouveaux clients.
            </p>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-final-cta-devis"
                className="group inline-flex items-center gap-3 rounded-full bg-up-sand text-up-brown px-9 sm:px-10 py-4 sm:py-5 text-[15px] sm:text-base font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-offwhite hover:shadow-[0_24px_50px_-20px_rgba(220,198,163,0.6)]"
              >
                Prendre rendez-vous
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <a
                href="tel:0695977072"
                data-testid="home-final-cta-phone"
                aria-label="Appeler Breizh Boost Studio"
                className="group inline-flex items-center gap-3 rounded-full border border-up-sand/60 text-up-offwhite bg-up-offwhite/10 backdrop-blur px-8 sm:px-9 py-4 sm:py-5 text-[15px] sm:text-base font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-sand hover:text-up-brown"
              >
                <Phone className="h-5 w-5" strokeWidth={2} />
                Appeler
              </a>
            </div>

            <p className="mt-8 text-xs sm:text-sm text-up-sand/70 italic">
              Premier échange gratuit · sans engagement
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
